import Backdrop from '@mui/material/Backdrop'
import Button from '@mui/material/Button'
import { PinturaDefaultImageWriterResult } from '@pqina/pintura'
import { PinturaEditor } from '@pqina/react-pintura'
import { ComposerMediaContext } from 'components/ComposerMediaContext/context'
import FileUpload from 'components/FileUpload'
import { ImageEditor } from 'components/ImageEditor'
import PPSelect, { PPSelectOptions } from 'components/PPSelect'
import SimpleLoader from 'components/SimpleLoader'
import { VideoEditor } from 'components/VideoEditorDialog/VideoEditor'
import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useMatch, useNavigate } from 'react-router'
import { addComposerImages, addComposerVideo, addImagePosts, promptCompose } from 'services/compose'
import { composerContentItemSelector, composerImageUrlsSelector, multiPostsArraySelector } from 'services/compose/selectors'
import { message } from 'services/snackbar'
import { setUploaderFiles, setUploadsDialogOpen } from 'services/uploads/actions'
import { StoreThunkDispatch } from 'store/state'
import { resetScroll } from 'utils/dom'
import ContentStudioGridWithFilters from './components/ContentStudioGridWithFilters'
import styles from './ContentStudio.pcss'

const EMPTY_IMAGE_URL = 'static/img/pesdk/pesdk-empty.jpg'
const TWITTER_CDN_HOSTNAME = 'pbs.twimg.com'

interface ContentStudioOwnProps {
  className?: string
  hideMultipost?: boolean
  popupView?: boolean
  onAddedToComposer?: () => void
}

export function ContentStudio(props: ContentStudioOwnProps) {
  const intl = useIntl()
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useDispatch<StoreThunkDispatch>()
  const match = useMatch('/content/studio/:editor')
  const [mode, setMode] = useState<'image' | 'video'>('image')
  const initialImagePath = `${window.location.origin}/${EMPTY_IMAGE_URL}`
  const [isCreatingFile, setIsCreatingFile] = useState(false)
  const [imageUrl, setImageUrl] = useState(location.state?.imageUrl || initialImagePath)
  const [selectedVideoUrl, setSelectedVideoUrl] = useState<string | undefined>(undefined)
  const [fileUpload, setFileUpload] = useState<File | null>(null)
  const imageEditorRef = useRef<PinturaEditor>(null)
  const videoEditorRef = useRef<PinturaEditor>(null)
  const composerContentItem = useSelector(composerContentItemSelector)
  const composerCarouselImages = useSelector(composerImageUrlsSelector)
  const multiPosts = useSelector(multiPostsArraySelector)
  const { dispatch: composerMediaDispatch } = useContext(ComposerMediaContext)

  useEffect(() => {
    const urlMode = match?.params.editor
    if (urlMode && ['image', 'video'].includes(urlMode)) {
      setMode(urlMode as 'image' | 'video')
    } else if (!props.popupView) {
      navigate('/content/studio/image')
    }
  }, [match?.params.editor, navigate, props.popupView])

  const modeOptions: PPSelectOptions = {
    image: { label: 'Image' },
    video: { label: 'Video' }
  }

  const onPhotoEditorError = useCallback((e: any) => {
    console.error('PhotoEditor error:', e)
    dispatch(message(intl.formatMessage({ id: 'general.media-editor.error' }), 'error'))
  }, [dispatch, intl])

  const setImage = useCallback((src: string) => {
    let url = src.indexOf('?') === -1 ? `${src}?v=${Date.now()}` : `${src}&v=${Date.now()}`
    // EXPL: Twitter images can't be loaded with url params
    if (src.indexOf(TWITTER_CDN_HOSTNAME) !== -1) {
      url = src
    }
    setImageUrl(url)
  }, [])

  useEffect(() => {
    if (location.state?.imageUrl) {
      setMode('image')
      navigate('/content/studio/image', { replace: true })
      setImage(location.state?.imageUrl)
    }
  }, [location.state?.imageUrl, setImage, navigate])

  const generateImage = useCallback(() => {
    return new Promise((resolve, reject) => {
      if (!imageEditorRef.current) {
        dispatch(message(intl.formatMessage({ id: 'errors.generic' }), 'error'))
        return reject('Editor not initialized!')
      }
      setIsCreatingFile(true)
      imageEditorRef.current?.editor.processImage()
        .then((result) => {
          setIsCreatingFile(false)
          resolve(result.dest)
        }).catch(() => {
          setIsCreatingFile(false)
          dispatch(message(intl.formatMessage({ id: 'general.media-editor.error' }), 'error'))
          reject('Error generating image.')
        })
    })
  }, [dispatch, intl])

  const generateVideo = useCallback(() => {
    return new Promise((resolve, reject) => {
      if (!videoEditorRef.current) {
        dispatch(message(intl.formatMessage({ id: 'errors.generic' }), 'error'))
        return reject('Editor not initialized!')
      }
      setIsCreatingFile(true)
      videoEditorRef.current?.editor.processImage()
        .then((result: PinturaDefaultImageWriterResult) => {
          setIsCreatingFile(false)
          resolve(result.dest)
        }).catch(() => {
          setIsCreatingFile(false)
          dispatch(message(intl.formatMessage({ id: 'general.media-editor.error' }), 'error'))
          reject('Error generating video.')
        })
    })
  }, [dispatch, intl])

  const addToComposer = () => {
    // EXPL: Open prompt if composer already has carousel or multi-post
    if (composerCarouselImages.length > 1 || multiPosts.length > 0) {
      dispatch(promptCompose({ content: {} as any, action: 'create' }))
      return
    }

    dispatch(message(intl.formatMessage({ id: `content-studio.notifications.creating-${mode}` })))
    const generateFile = mode === 'image' ? generateImage : generateVideo
    generateFile().then((file: File) => {
      if (file.type.includes('video')) {
        dispatch(addComposerVideo({ video: { file, isEdited: true }, contextDispatch: composerMediaDispatch }))
      } else {
        dispatch(addComposerImages({
          items: [{ file, isEdited: true }],
          contextDispatch: composerMediaDispatch
        }))
      }

      if (props.onAddedToComposer) {
        props.onAddedToComposer()
      } else {
        navigate('/composer', { state: { floating: true, modal: 'compose', opener: location.pathname, background: location } })
      }
    })
  }

  const addMultiPost = () => {
    generateImage().then((file: File) => {
      if (!composerContentItem && composerCarouselImages.length === 0) {
        dispatch(message(intl.formatMessage({ id: 'content-studio.notifications.creating-image' })))
        setFileUpload(file)
      } else {
        dispatch(promptCompose({ content: {} as any, action: 'multipost' }))
      }
    })
  }

  const onFileUploaded = (url: string) => {
    setFileUpload(null)
    dispatch(addImagePosts([{ url }]))
    if (props.onAddedToComposer) {
      props.onAddedToComposer()
    } else {
      navigate('/composer', { state: { floating: true, modal: 'compose', opener: location.pathname, background: location } })
    }
  }

  const onFileUploadError = () => {
    dispatch(message(intl.formatMessage({ id: 'content-studio.notifications.upload-error' }), 'error'))
    setFileUpload(null)
  }

  const saveToLibrary = () => {
    switch (mode) {
      case 'image':
        dispatch(message(intl.formatMessage({ id: 'content-studio.notifications.creating-image' })))
        generateImage().then((file: File) => {
          dispatch(setUploaderFiles({ [file.name]: file }))
          dispatch(setUploadsDialogOpen(true))
        })
        break

      case 'video':
        dispatch(message(intl.formatMessage({ id: 'content-studio.notifications.creating-video' })))
        generateVideo().then((file: File) => {
          dispatch(setUploaderFiles({ [file.name]: file }))
          dispatch(setUploadsDialogOpen(true))
        })
        break
    }
  }

  const onModeChange = (newMode: 'image' | 'video') => {
    setMode(newMode)
    navigate(`/content/studio/${newMode}`, { replace: true })
  }

  const onVideoSelected = (videoUrl: string) => {
    setSelectedVideoUrl(videoUrl)
    resetScroll()
  }

  const onImageSelected = (imageUrl: string) => {
    setImage(imageUrl)
    resetScroll()
  }

  return (
    <div className={`${styles.wrapper} ${props.className || ''}`}>
      <header className={styles.header}>
        <h1 className={styles.title}>
          <FormattedMessage
            id="content-studio.title"
            values={{
              select: () => (
                <PPSelect
                  options={modeOptions}
                  selectedValue={mode}
                  withCaret
                  buttonClassName={styles['header-btn']}
                  className={styles.select}
                  noLabelCaps
                  raised
                  onSelectionChange={onModeChange}
                />
              )
            }}
          />
        </h1>
        <div className={styles.actions}>
          <Button variant="text" disabled={isCreatingFile} onClick={saveToLibrary}>
            <FormattedMessage id="content-studio.actions.save-to-library" />
          </Button>
          {mode === 'image' && !props.hideMultipost && (
            <Button variant="contained" color="primary" disabled={isCreatingFile} onClick={addMultiPost}>
              <FormattedMessage id="content-studio.actions.multi-post" />
            </Button>
          )}
          <Button variant="contained" color="primary" disabled={isCreatingFile} onClick={addToComposer}>
            <FormattedMessage id="content-studio.actions.add-to-composer" />
          </Button>
        </div>
      </header>
      <div className={styles['editor-box']}>
        {mode === 'image' && (
          <ImageEditor
            imageUrl={imageUrl}
            className={styles.editor}
            hideSaveButton
            onLoadError={onPhotoEditorError}
            ref={imageEditorRef}
          />
        )}
        {mode === 'video' && (
          <VideoEditor ref={videoEditorRef} videoUrl={selectedVideoUrl} hideSaveButton />
        )}
      </div>
      <div className={styles['content-box']}>
        <ContentStudioGridWithFilters mode={mode} onImageSelected={onImageSelected} onVideoSelected={onVideoSelected} />
      </div>
      <Backdrop open={isCreatingFile || Boolean(fileUpload)} className={styles.backdrop}>
        <SimpleLoader />
      </Backdrop>
      {fileUpload && (
        <FileUpload
          file={fileUpload}
          rotateBy="0"
          skipValidation
          onFileUploaded={onFileUploaded}
          onError={onFileUploadError}
        />
      )}
    </div>
  )
}

export default ContentStudio
